import React, { useState, useRef, useEffect } from "react";

const QuestionDropDown = () => {
  const options = [
    "Show Me New Questions Only",
    "Repeat Questions Marked Incorrect",
  ];
  const [selectedItem, setSelectedItem] = useState(options[0]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Keyboard accessibility
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

  const handleSelect = (option) => {
    setSelectedItem(option);
    setIsOpen(false);
  };

  // CSS Styles
  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "24px",
    },
    dropdown: {
      position: "relative",
      width: "280px",
      maxWidth: "100%",
    },
    button: {
      width: "100%",
      padding: "12px 16px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      border: isOpen ? "1px solid #3b82f6" : "1px solid #d1d5db",
      borderRadius: "8px",
      boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
      backgroundColor: "white",
      cursor: "pointer",
      outline: "none",
      fontSize: "14px",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
    },
    buttonHover: {
      borderColor: "#a0aec0",
    },
    buttonFocus: {
      boxShadow: isOpen ? "0 0 0 3px rgba(59, 130, 246, 0.3)" : "none",
    },
    arrow: {
      width: "16px",
      height: "16px",
      transition: "transform 0.2s ease",
      transform: isOpen ? "rotate(180deg)" : "rotate(0)",
    },
    menu: {
      position: "absolute",
      width: "100%",
      marginTop: "6px",
      border: "1px solid #e2e8f0",
      borderRadius: "8px",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      backgroundColor: "white",
      zIndex: 10,
      overflow: "hidden",
    },
    option: {
      padding: "12px 16px",
      cursor: "pointer",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      transition: "background-color 0.15s ease",
    },
    optionSelected: {
      backgroundColor: "#ebf5ff",
      color: "#1e40af",
    },
    checkIcon: {
      display: "inline-block",
      width: "16px",
      height: "16px",
      color: "#2563eb",
    },
  };

  // Hover state management
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <div style={styles.container}>
      <div style={styles.dropdown} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          style={{
            ...styles.button,
            ...(isOpen ? styles.buttonFocus : {}),
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-labelledby="question-selection"
        >
          <span>{selectedItem}</span>
          <svg
            style={styles.arrow}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>

        {isOpen && (
          <div style={styles.menu} role="listbox">
            {options.map((option, index) => (
              <div
                key={option}
                onClick={() => handleSelect(option)}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                role="option"
                aria-selected={selectedItem === option}
                tabIndex={0}
                style={{
                  ...styles.option,
                  ...(selectedItem === option ? styles.optionSelected : {}),
                  backgroundColor:
                    hoveredIndex === index && selectedItem !== option
                      ? "#f7fafc"
                      : selectedItem === option
                      ? "#ebf5ff"
                      : "white",
                }}
              >
                <span>{option}</span>
                {selectedItem === option && (
                  <svg
                    style={styles.checkIcon}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionDropDown;
